import { useNotification } from "@refinedev/core";
import { useRef } from "react";

import { AxiosResponse } from "axios";

/**
 * A type representing the common response structure from your API
 * Compatible with AxiosResponse from axios
 */
export type ApiResponse<T> = AxiosResponse<
  {
    data: T;
    [key: string]: unknown;
  },
  unknown
>;

/**
 * Options for the safeApiCall function
 */
interface SafeApiCallOptions {
  /** Error message to display in notification if API call fails */
  errorMessage?: string;
  /** Whether to suppress error notifications */
  suppressErrorNotification?: boolean;
}

/**
 * A hook that creates a function to safely call APIs with concurrency control
 * and standard error handling.
 *
 * This handles:
 * 1. Making the API call
 * 2. Extracting .data.data automatically
 * 3. Managing request versioning to prevent stale updates
 * 4. Standard error handling and notifications
 * 5. Type-safety throughout
 *
 * @returns A function that handles API calls safely
 */
export function useSafeApiCall() {
  const { open } = useNotification();
  // Track request versions by function name
  const requestVersionsRef = useRef<Map<string, number>>(new Map());

  /**
   * Safely calls an API and handles the response with concurrency control
   *
   * @param apiFunction The API function to call
   * @param params Parameters to pass to the API function
   * @param setStateFunction Function to update state with the result
   * @param options Additional options
   * @returns Promise resolving to the extracted data on success, null on failure
   */
  return async function safeApiCall<T, P extends unknown[]>(
    apiFunction: (...args: P) => Promise<ApiResponse<T>>,
    params: P,
    setStateFunction: (data: T) => void,
    options: SafeApiCallOptions = {},
    events?: {
      onFail?: () => void;
    }
  ): Promise<T | null> {
    // Get function name to use as the key (or a generated ID if none)
    const funcKey =
      apiFunction.name ||
      `anonymous_func_${Math.random().toString(36).substring(2, 9)}`;

    // Initialize counter for this function if it doesn't exist
    if (!requestVersionsRef.current.has(funcKey)) {
      requestVersionsRef.current.set(funcKey, 0);
    }

    // Increment the counter for this specific function
    const newVersion = (requestVersionsRef.current.get(funcKey) || 0) + 1;
    requestVersionsRef.current.set(funcKey, newVersion);

    // Remember this request's version
    const thisRequestVersion = newVersion;

    try {
      // Make the API call
      const response = await apiFunction(...params);

      // Check if this is still the most recent request for this function
      if (thisRequestVersion !== requestVersionsRef.current.get(funcKey)) {
        console.info(
          `Ignoring stale API response for ${funcKey} due to newer request`
        );
        return null;
      }

      let data: unknown = undefined;

      // Check if the expected data exists
      if (response?.data?.data) {
        // Return the data for optional chaining
        data = response.data.data;
      } else if (response?.data) {
        // Return the data for optional chaining
        data = response.data as T;
      }

      if (data) {
        const status = (data as { status: string }).status;

        if (status === "ERROR") {
          throw new Error((data as { message: string }).message);
        }

        setStateFunction(data as T);

        return data as T;
      }

      throw new Error("No data found");
    } catch (error) {
      // Log the error
      console.error(`Error in API call ${apiFunction.name}:`, error);

      // Show notification if not suppressed
      if (!options.suppressErrorNotification) {
        open?.({
          message: options.errorMessage || "An error occurred",
          type: "error",
        });
      }

      if (events?.onFail) {
        events.onFail();
      }

      // Re-throw the error to allow calling code to handle it
      throw error;
    }
  };
}
